import { useState, useCallback, useEffect, useRef } from 'react';
import { sendMessage as apiSendMessage, getConversations, getConversation, getLatestConversation } from '@/api/ai';
import type { ConversationMessage } from '@/api/ai';
import type { AIToolType } from '@/types/common';
import { useQuery } from '@tanstack/react-query';

export const aiKeys = {
  all: ['ai'] as const,
  conversations: () => [...aiKeys.all, 'conversations'] as const,
  conversation: (id: string) => [...aiKeys.all, 'conversation', id] as const,
};

export interface UseAIOptions {
  // Opt-in: hydrate `messages` from the newest persisted conversation for this
  // tool_type on mount. DEFAULT OFF. Only surfaces that render a *continuing*
  // chat (e.g. Industry Intel) should enable this. One-shot / ephemeral tools
  // (deal_review, contract_analysis, the AIPanel) must stay empty, because
  // `messages` is sent as conversation_history on the next send (see
  // sendMessage below) — auto-hydrating them would inject stale history into
  // their AI requests.
  loadHistory?: boolean;
}

export function useAI(toolType: AIToolType, options?: UseAIOptions) {
  const loadHistory = options?.loadHistory ?? false;
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Start hydrating immediately when opted in, so the consumer can disable its
  // composer on the very first render (no flash of an enabled input before the
  // effect runs). Always false when loadHistory is off.
  const [isHydrating, setIsHydrating] = useState(loadHistory);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Marks the conversation as user-driven (a send happened, or it was explicitly
  // cleared). A late-resolving hydration must never stomp a live/cleared convo.
  const touchedRef = useRef(false);

  const sendMessage = useCallback(
    async (
      content: string,
      additionalContext?: Record<string, unknown>,
      extras?: { deal_id?: string }
    ) => {
      // Any user send makes this a live conversation — block hydration clobber.
      touchedRef.current = true;
      const userMessage: ConversationMessage = {
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiSendMessage(
          toolType,
          content,
          messages,
          additionalContext,
          extras
        );

        const assistantMessage: ConversationMessage = {
          role: 'assistant',
          content: response.message,
          timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, assistantMessage]);
        setConversationId(response.conversation_id);
        return response;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to send message');
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [toolType, messages]
  );

  // Opt-in history hydration. Runs once per (loadHistory, toolType). When off,
  // no fetch happens and `messages` stays [] exactly as before.
  useEffect(() => {
    if (!loadHistory) return;
    let cancelled = false;
    setIsHydrating(true);
    getLatestConversation(toolType)
      .then((res) => {
        // Don't stomp a live/cleared conversation: only hydrate the INITIAL
        // empty state, and only if this effect hasn't been torn down.
        if (cancelled || touchedRef.current) return;
        const convo = res.conversation;
        if (convo && Array.isArray(convo.messages) && convo.messages.length > 0) {
          setMessages(convo.messages);
          setConversationId(convo.id);
        }
        // null / empty → leave messages [] so the caller's EmptyState renders.
      })
      .catch(() => {
        // Non-fatal: a failed hydration must not block starting a fresh chat.
      })
      .finally(() => {
        if (!cancelled) setIsHydrating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadHistory, toolType]);

  function clearConversation() {
    touchedRef.current = true; // explicit New chat — no late re-hydration
    setMessages([]);
    setConversationId(null);
    setError(null);
  }

  return {
    messages,
    isLoading,
    isHydrating,
    conversationId,
    error,
    sendMessage,
    clearConversation,
  };
}

export function useConversations() {
  return useQuery({
    queryKey: aiKeys.conversations(),
    queryFn: getConversations,
  });
}

export function useConversation(id: string) {
  return useQuery({
    queryKey: aiKeys.conversation(id),
    queryFn: () => getConversation(id),
    enabled: !!id,
  });
}
